import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@material-ui/core';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import Alert from '@material-ui/lab/Alert';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import { useCreateBundlePricingManagementMutation, useUpdateBundlePricingManagementMutation } from 'features/pricing/pricingSlice';
import { CREATE_BUNDLE_STEPS, CREATE_BUNDLE_STEP_INDEX, CREATE_BUNDLE_STEP_WARNINGS, buildCreateBundleValidationData, CREATE_BUNDLE_STEP_KEYS } from './createBundleConsts';
import { setBundleCurrentStep, cleanupBundleStepDraft, resetCreateBundleState } from './bundleStateSlice';
import { useCreateActivityDialogStyles } from '../CreateActivityAndPricing/CreateActivityAndPricingDialogStyle';
import LoadingSpinner from 'components/LoadingSpinner';
import useGetBundleData from './hooks/useGetBundleData';
import { createBundle, updateBundleSave, hasAnyBundleStepChanges } from './utils/bundleSaveOperations';

const BUNDLE_DIALOG_MODES = {
    CREATE: 'create',
    EDIT: 'edit',
};

const BundleDialog = ({ open, handleClose, mode = BUNDLE_DIALOG_MODES.CREATE, bundleId = null, prefill = null }) => {
    const classes = useCreateActivityDialogStyles();
    const dispatch = useDispatch();
    const isEditMode = mode === BUNDLE_DIALOG_MODES.EDIT;

    const onCloseDialog = () => {
        handleClose();
        dispatch(resetCreateBundleState());
    };

    const { isDataLoaded } = useGetBundleData({
        bundleId,
        open,
        enabled: isEditMode,
        prefill,
    });

    const [createBundlePricingManagement, { isLoading: isCreating }] = useCreateBundlePricingManagementMutation();
    const [updateBundlePricingManagement, { isLoading: isUpdating }] = useUpdateBundlePricingManagementMutation();

    const currentStep = useSelector(state => state.bundleState.currentStep);
    const stepsData = useSelector(state => state.bundleState.steps);
    const originalSteps = useSelector(state => state.bundleState.originalSteps);

    const hasChanges = isEditMode ? hasAnyBundleStepChanges(originalSteps, stepsData) : true;

    const handleSave = async () => {
        if (isEditMode) {
            await updateBundleSave({
                stepsData,
                originalSteps,
                bundleId,
                updateMutation: updateBundlePricingManagement,
                onSuccess: onCloseDialog
            });
        } else {
            await createBundle({
                stepsData,
                createMutation: createBundlePricingManagement,
                onSuccess: onCloseDialog
            });
        }
    };

    const isSaving = isEditMode ? isUpdating : isCreating;
    const saveDisabled = isEditMode && !hasChanges;
    const saveDisabledTooltip = saveDisabled ? "No changes to save" : "";
    
    const bundleName = stepsData[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS]?.name || '';
    const title = isEditMode 
        ? `Edit Bundle${bundleName ? ` - ${bundleName}` : ''}` 
        : "Create Bundle";
    
    const saveButtonText = isEditMode ? "Save Changes" : "Create Bundle";
    const savingButtonText = isEditMode ? "Saving..." : "Creating...";

    const currentStepConfig = CREATE_BUNDLE_STEPS[currentStep];
    const validationData = buildCreateBundleValidationData(stepsData);
    const isStepValid = currentStepConfig?.validate ? currentStepConfig.validate(validationData) : true;

    const isEditingPricing = validationData.pricing?.pricingItems?.some(item => item.isNew) || validationData.pricing?.somePricingHasUnsavedEdits;
    const hasNoPricingConfigured = (validationData.pricing?.pricingItems?.length || 0) === 0;
    const isEditingRule = validationData.rules?.rulesList?.some(rule => rule.isNew) || validationData.rules?.someRuleHasUnsavedEdits;
    const hasNoRulesConfigured = (validationData.rules?.rulesList?.length || 0) === 0;

    const moveToStep = (nextStep) => {
        dispatch(setBundleCurrentStep(nextStep));
    };

    const handleContinueOrSkip = () => {
        if (!isStepValid && currentStep === CREATE_BUNDLE_STEP_INDEX.PRICING) {
            dispatch(cleanupBundleStepDraft({ stepKey: CREATE_BUNDLE_STEP_KEYS.PRICING }));
        } else if (!isStepValid && currentStep === CREATE_BUNDLE_STEP_INDEX.RULES) {
            dispatch(cleanupBundleStepDraft({ stepKey: CREATE_BUNDLE_STEP_KEYS.RULES }));
        }
        moveToStep(currentStep + 1);
    };

    const getStepWarning = () => {
        if (currentStep === CREATE_BUNDLE_STEP_INDEX.PRICING && isEditingPricing) {
            return CREATE_BUNDLE_STEP_WARNINGS.PRICING_EDITING;
        }
        if (currentStep === CREATE_BUNDLE_STEP_INDEX.PRICING && hasNoPricingConfigured) {
            return CREATE_BUNDLE_STEP_WARNINGS.NO_PRICING_CONFIGURED;
        }
        if (currentStep === CREATE_BUNDLE_STEP_INDEX.RULES && isEditingRule) {
            return CREATE_BUNDLE_STEP_WARNINGS.RULE_EDITING;
        }
        if (currentStep === CREATE_BUNDLE_STEP_INDEX.RULES && hasNoRulesConfigured) {
            return CREATE_BUNDLE_STEP_WARNINGS.NO_RULES_CONFIGURED;
        }
        return CREATE_BUNDLE_STEP_WARNINGS.MISSING_FIELDS;
    };

    const totalSteps = CREATE_BUNDLE_STEPS.length;
    const isBundleDetailsStep = currentStep === CREATE_BUNDLE_STEP_INDEX.BUNDLE_DETAILS;
    const isContinueDisabled = ((isBundleDetailsStep || currentStep === CREATE_BUNDLE_STEP_INDEX.PRICING) && !isStepValid);

    const tooltipText =
        isBundleDetailsStep && !isStepValid
            ? CREATE_BUNDLE_STEP_WARNINGS.BUNDLE_DETAILS_REQUIRED
            : !isStepValid
                ? getStepWarning()
                : '';

    return (
        <Authorize profile={permissionProfiles.PRICING.MANAGE_PPRICING}>
            <Dialog maxWidth='lg' fullWidth open={open} onClose={onCloseDialog} aria-labelledby='bundle-dialog-title'>
                <DialogTitle id='bundle-dialog-title'>
                    {title}
                </DialogTitle>
                <DialogContent className={classes.dialogContent} style={{ minHeight: '400px', paddingTop: 16 }}>
                    {isEditMode && !isDataLoaded ? (
                        <LoadingSpinner loading={true} />
                    ) : (
                        <>
                            <Box className={classes.stepperContainer}>
                                <Stepper activeStep={currentStep}>
                                    {CREATE_BUNDLE_STEPS.map((stepConfig) => (
                                        <Step key={stepConfig.key}>
                                            <StepLabel>
                                                <div>{stepConfig.title}</div>
                                                <div className={classes.stepSubtitle}>{stepConfig.subtitle}</div>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                            {currentStepConfig?.component({ onEditStep: moveToStep })}
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    {!isDataLoaded ? null : (
                        <>
                            {currentStep !== 0 && (
                                <Button
                                    className={classes.backButton}
                                    startIcon={<ArrowBackIosIcon />}
                                    onClick={() => moveToStep(currentStep - 1)}
                                >
                                    Back
                                </Button>
                            )}
                            {!isStepValid && !isBundleDetailsStep && currentStep !== totalSteps - 1 && (
                                <Alert severity="warning" className={classes.warningAlert}>
                                    {getStepWarning()}
                                </Alert>
                            )}
                            <Box className={classes.rightButtonContainer}>
                                {currentStep !== totalSteps - 1 ? (
                                    <Tooltip
                                        title={tooltipText}
                                        placement="top"
                                    >
                                        <span>
                                            <Button
                                                className={classes.continueButton}
                                                variant='contained'
                                                endIcon={<ArrowForwardIosIcon />}
                                                onClick={handleContinueOrSkip}
                                                disabled={isContinueDisabled}
                                            >
                                                {isStepValid || isBundleDetailsStep ? 'Continue' : 'Skip'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={saveDisabledTooltip}
                                        placement="top"
                                    >
                                        <span>
                                            <Button
                                                className={classes.continueButton}
                                                variant='contained'
                                                onClick={handleSave}
                                                disabled={isSaving || saveDisabled}
                                            >
                                                {isSaving ? savingButtonText : saveButtonText}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Authorize>
    );
};

export { BUNDLE_DIALOG_MODES };
export default BundleDialog;
